import React, { useState, useEffect } from 'react';
import { Plus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { salesPerformanceService } from '../../services/salesPerformanceService';
import { useAuth } from '../../contexts/AuthContext';
import MonthlyDataForm from '../sales-performance-tracker/components/MonthlyDataForm';
import MonthlyDataTable from '../sales-performance-tracker/components/MonthlyDataTable';

export default function SalesPerformanceMonthlyData() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const recordsData = await salesPerformanceService?.getAll();
      setRecords(recordsData);
    } catch (err) {
      setError(err?.message || 'Failed to load monthly performance data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingRecord) {
        await salesPerformanceService?.update(editingRecord?.id, data);
      } else {
        await salesPerformanceService?.create(data);
      }
      
      await loadData();
      setShowForm(false);
      setEditingRecord(null);
    } catch (err) {
      throw new Error(err.message || 'Failed to save data');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    try {
      await salesPerformanceService?.delete(id);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to delete record');
    }
  };

  const formatCurrency = (amount, currency = null) => {
    const displayCurrency = currency || profile?.currency || 'USD';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const calculateMonthlyProfitLoss = (record) => {
    const totalRevenue = (record?.existingClientRevenue || 0) + (record?.newClientRevenue || 0);
    const totalCosts = (record?.otherMonthlyCosts || 0) + (record?.googleAdsExpense || 0);
    const grossProfit = totalRevenue * ((record?.grossProfitPercentage || 0) / 100);
    const netProfit = grossProfit - totalCosts;
    const profitMarginPercentage = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    
    return {
      totalRevenue,
      totalCosts,
      grossProfit,
      netProfit,
      profitMarginPercentage,
      isProfit: netProfit >= 0
    };
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading monthly performance data...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            {/* Header with Back Button */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/sales-performance-tracker')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="text-sm font-medium">Back to Overview</span>
              </button>
              
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Monthly Performance Data</h1>
                  <p className="text-sm sm:text-base text-gray-600 mt-1">Detailed breakdown of each month's performance</p>
                </div>
                <button
                  onClick={() => {
                    setEditingRecord(null);
                    setShowForm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={20} />
                  Add Monthly Data
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {/* Monthly Performance Data Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <MonthlyDataTable
                records={records}
                onEdit={handleEdit}
                onDelete={handleDelete}
                formatCurrency={formatCurrency}
                calculateMonthlyProfitLoss={calculateMonthlyProfitLoss}
              />
            </div>
          </div>
        </main>
      </div>
      
      {/* Monthly Data Form Modal */}
      {showForm && (
        <MonthlyDataForm
          record={editingRecord}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          userCurrency={profile?.currency || 'USD'}
        />
      )}
    </div>
  );
}